import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SnackbarKey, useSnackbar } from "notistack";
import { useNavigate } from "react-router";
import {
  CsvImportPerformAttributesBankSlugEnum,
  CsvImportPerformAttributesTypeEnum,
  CsvRow,
  ImportCsvFromFileAsyncBankSlugEnum,
  useImportCsvFromRecordsAsync,
  useImportCsvStatus,
} from "@rentablo/api/apiv3";
import { parseOrUndefined } from "@helpers";
import { useCsvImportFile } from "@api/v3/hooks/useCsvImportFile";

const isFileImportBankSlug = (
  slug: string
): slug is ImportCsvFromFileAsyncBankSlugEnum =>
  Object.values(ImportCsvFromFileAsyncBankSlugEnum).includes(
    slug as ImportCsvFromFileAsyncBankSlugEnum
  );

export const useAsyncCsvImport = (
  bankSlug: CsvImportPerformAttributesBankSlugEnum | undefined,
  depotId?: number,
  type?: CsvImportPerformAttributesTypeEnum
) => {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { mutate: startAsyncImport, isPending: isStartingAsync } =
    useImportCsvFromRecordsAsync();
  const { mutate: startAsyncFileImport, isPending: isStartingAsyncFile } =
    useCsvImportFile();
  const [batchId, setBatchId] = useState<string | null>(null);

  const importStatus = useImportCsvStatus(
    { batchId: batchId! },
    {
      enabled: !!batchId,
      refetchInterval: (query) =>
        query.state.data?.status === "IN_PROGRESS" ? 1500 : false,
    }
  );

  const statusValue = importStatus.data?.status;
  const isImporting =
    isStartingAsync ||
    isStartingAsyncFile ||
    (!!batchId && (!statusValue || statusValue === "IN_PROGRESS"));

  const handledStatusRef = useRef<string | null>(null);
  const waitingSnackbarRef = useRef<SnackbarKey | null>(null);
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isImporting && !pollingTimerRef.current) {
      pollingTimerRef.current = setTimeout(() => {
        waitingSnackbarRef.current = enqueueSnackbar(
          t("csvImport.parsingResults.savePending"),
          {
            variant: "info",
            persist: true,
          }
        );
      }, 3000);
    }

    if (!isImporting) {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      if (waitingSnackbarRef.current) {
        closeSnackbar(waitingSnackbarRef.current);
        waitingSnackbarRef.current = null;
      }
    }

    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      if (waitingSnackbarRef.current) {
        closeSnackbar(waitingSnackbarRef.current);
        waitingSnackbarRef.current = null;
      }
    };
  }, [isImporting, enqueueSnackbar, closeSnackbar, t]);

  useEffect(() => {
    if (!statusValue || !depotId || handledStatusRef.current === batchId)
      return;

    if (statusValue === "SUCCESS") {
      handledStatusRef.current = batchId;
      enqueueSnackbar(t("csvImport.parsingResults.saveSuccess"), {
        variant: "success",
        autoHideDuration: 3000,
      });
      const successUrl =
        type === "cash_transaction"
          ? `/accounts/${depotId}`
          : `/depots/${depotId}`;
      navigate(successUrl);
    }

    if (statusValue === "FAILED") {
      handledStatusRef.current = batchId;
      enqueueSnackbar(t("csvImport.parsingResults.saveError"), {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  }, [statusValue, batchId, depotId, type, navigate, enqueueSnackbar, t]);

  const submitImport = (
    parsingResults: CsvRow[],
    type: CsvImportPerformAttributesTypeEnum,
    balance?: string
  ) => {
    if (!depotId || !bankSlug) return;

    startAsyncImport(
      {
        csvImportPerformAttributes: {
          records: parsingResults,
          // @ts-ignore
          depot_id: depotId,
          type,
          bank_slug: bankSlug,
          balance: balance ? parseOrUndefined(balance) : undefined,
        },
      },
      {
        onSuccess: (response) => {
          if (response.batch_id) {
            setBatchId(response.batch_id);
          }
        },
        onError: () => {
          enqueueSnackbar(t("csvImport.parsingResults.saveError"), {
            variant: "error",
            autoHideDuration: 3000,
          });
        },
      }
    );
  };

  const submitFileImport = (file: File) => {
    if (!depotId || !bankSlug || !isFileImportBankSlug(bankSlug)) return;

    startAsyncFileImport(
      {
        file,
        depotId: String(depotId),
        bankSlug,
      },
      {
        onSuccess: (response) => {
          if (response.batch_id) {
            setBatchId(response.batch_id);
          }
        },
        onError: () => {
          enqueueSnackbar(t("csvImport.parsingResults.saveError"), {
            variant: "error",
            autoHideDuration: 3000,
          });
        },
      }
    );
  };

  return { isImporting, submitImport, submitFileImport };
};
